import { MATERIAL_UNITS, type MaterialClass } from '@felix/domain';
import {
  Button,
  Checkbox,
  Modal,
  NumberInput,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconClipboardText, IconListSearch, IconPlus, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import type { Material } from '../materials/types.js';
import { errorCode } from '../personnel/source.js';
import { catalogMatch, type PastedRow, parsePastedLines, remainingLines } from './bulk-lines.js';
import classes from './Purchasing.module.css';
import { formatQuantity } from './requisitions.js';
import type { PurchasingSource } from './source.js';
import { LoadError } from './table.js';

const UNITS = [...MATERIAL_UNITS];

export interface PickedItem {
  materialId: string | null;
  name: string;
  unit: string;
  quantity: number;
  /** Gemide geminin stok mevcudu (ADR 0111 §A); bilinmiyorsa yok. */
  onHand?: number | null;
}

/** Yapıştırılan satırın önerisi: katalog kalemi ya da serbest kalem; kullanıcı onaylar. */
interface Proposal {
  row: PastedRow;
  material: Material | null;
  include: boolean;
  quantity: number | '';
  unit: string;
}

/**
 * "Add Material" (`satin-alma-akisi.jpeg` "New Material Requisition"): katalogdan kalemler
 * seçilir (onay kutusu ve miktar, ortağın notu #6) ya da Excel'den yapıştırılır; katalogda
 * olmayan kalem serbest metinle eklenir (sorular #73). Kalemin adı ve birimi talebe kopyalanır.
 * Yapıştırmada eşleşmeyen satır serbest kalem önerisidir; kullanıcı satır satır onaylar (ADR 0047
 * §3). Talep en çok 200 satırdır.
 */
export function MaterialPicker({
  opened,
  source,
  existing,
  onClose,
  onAdd,
  catalogOnly = false,
  materialClass,
}: {
  opened: boolean;
  source: PurchasingSource;
  /** Talepteki satır sayısı: 200 sınırına kalan yer. */
  existing: number;
  onClose: () => void;
  onAdd: (items: PickedItem[]) => void;
  /**
   * Yalnız katalog kalemi (ADR 0111 §A, sorular #1172): ofis revizyonunda serbest kalem ve
   * yapıştırma yok.
   */
  catalogOnly?: boolean;
  /**
   * Katalog listesinin havuzu (ADR 0122 §6): talebin bölümünden; Provisions talebinde kumanya,
   * öteki bölümlerde stores & spares. Yapıştırılan kod süzülmez (kod açık seçimdir).
   */
  materialClass?: MaterialClass | undefined;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.picker.${key}`, options);
  const [tab, setTab] = useState<string | null>('catalogue');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<Material[] | null>(null);
  // Katalog okunamadıysa "eşleşen kalem yok" denmez (M6f §0.6): kullanıcı kalemi yanlışlıkla
  // serbest metinle eklemesin, katalog bağı kopmasın
  const [failure, setFailure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  /** Seçilen katalog kalemleri; arama değişse de korunur. */
  const [selected, setSelected] = useState<Map<string, { item: Material; quantity: number | '' }>>(
    () => new Map(),
  );
  const [freeName, setFreeName] = useState('');
  const [freeUnit, setFreeUnit] = useState<string | null>('PCS');
  const [pasted, setPasted] = useState('');
  const [proposals, setProposals] = useState<Proposal[] | null>(null);
  const [parseNote, setParseNote] = useState<{ header: boolean; truncated: boolean } | null>(null);
  const [matching, setMatching] = useState(false);
  const [matchError, setMatchError] = useState<string | null>(null);
  const remaining = remainingLines(existing);

  useEffect(() => {
    if (!opened || tab !== 'catalogue') return;
    let alive = true;
    void attempt;
    const handle = setTimeout(() => {
      setItems(null);
      setFailure(null);
      source
        .catalog({
          ...(query.trim() ? { q: query.trim() } : {}),
          ...(materialClass ? { materialClass } : {}),
          page: 1,
          pageSize: 10,
        })
        .then((list) => {
          if (alive) setItems(list.items);
        })
        .catch((error: unknown) => {
          if (alive) setFailure(errorCode(error));
        });
    }, 200);
    return () => {
      alive = false;
      clearTimeout(handle);
    };
  }, [opened, query, source, attempt, tab, materialClass]);

  const close = () => {
    setQuery('');
    setFreeName('');
    setSelected(new Map());
    setPasted('');
    setProposals(null);
    setParseNote(null);
    setMatchError(null);
    setTab('catalogue');
    onClose();
  };

  const toggle = (item: Material, checked: boolean) =>
    setSelected((current) => {
      const next = new Map(current);
      if (checked) next.set(item.id, { item, quantity: next.get(item.id)?.quantity ?? 1 });
      else next.delete(item.id);
      return next;
    });

  const chosen = [...selected.values()];
  const catalogueValid = chosen.length > 0 && chosen.every((c) => typeof c.quantity === 'number');
  const addCatalogue = () => {
    onAdd(
      chosen.map(({ item, quantity }) => ({
        materialId: item.id,
        name: item.name,
        unit: item.unit,
        quantity: Number(quantity),
        ...(item.onHand === undefined ? {} : { onHand: item.onHand }),
      })),
    );
    close();
  };

  /** Yapıştırılan satırları okur ve her kod/ad için katalogda arar (sırayla; gemide yerel). */
  const match = async () => {
    const parsed = parsePastedLines(pasted);
    setParseNote({ header: parsed.skippedHeader, truncated: parsed.truncated });
    setMatching(true);
    setMatchError(null);
    try {
      const cache = new Map<string, Material[]>();
      const next: Proposal[] = [];
      for (const row of parsed.rows) {
        const lookup = row.code.toLowerCase();
        let candidates = cache.get(lookup);
        if (!candidates) {
          candidates = (await source.catalog({ q: row.code, page: 1, pageSize: 10 })).items;
          cache.set(lookup, candidates);
        }
        const material = catalogMatch(row.code, candidates);
        next.push({
          row,
          material,
          // Katalog kalemi hazır gelir; serbest kalem kullanıcının onayını bekler
          include: material !== null && row.quantity !== null,
          quantity: row.quantity ?? '',
          unit: material?.unit ?? row.unit ?? 'PCS',
        });
      }
      setProposals(next);
    } catch (error) {
      setMatchError(errorCode(error));
    } finally {
      setMatching(false);
    }
  };

  const included = (proposals ?? []).filter((p) => p.include);
  const pasteValid =
    included.length > 0 && included.every((p) => typeof p.quantity === 'number' && p.quantity > 0);
  const setProposal = (index: number, patch: Partial<Proposal>) =>
    setProposals((current) =>
      (current ?? []).map((p, i) => (i === index ? { ...p, ...patch } : p)),
    );
  const addPasted = () => {
    onAdd(
      included.map((p) => ({
        materialId: p.material?.id ?? null,
        name: p.material?.name ?? p.row.code.slice(0, 200),
        unit: p.unit,
        quantity: Number(p.quantity),
      })),
    );
    close();
  };

  const limitNote = (count: number) =>
    count > remaining ? (
      <p className={classes.error} role="alert">
        {k('limit', { count: remaining })}
      </p>
    ) : null;

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={k('title')}
      size="xl"
      closeButtonProps={{ 'aria-label': k('close') }}
    >
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="catalogue" leftSection={<IconListSearch size={16} />}>
            {k('catalogueTab')}
          </Tabs.Tab>
          {catalogOnly ? null : (
            <Tabs.Tab value="paste" leftSection={<IconClipboardText size={16} />}>
              {k('pasteTab')}
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="catalogue" pt="sm">
          <TextInput
            label={k('search')}
            placeholder={k('searchPlaceholder')}
            leftSection={<IconSearch size={16} aria-hidden />}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            data-autofocus
          />
          {failure ? (
            <LoadError
              title={k('error')}
              code={failure}
              onRetry={() => setAttempt((value) => value + 1)}
            />
          ) : items === null ? (
            <Skeleton height={180} mt="sm" />
          ) : items.length === 0 ? (
            <EmptyState title={k('none')} />
          ) : (
            <ul className={classes.pickerList} aria-label={k('results')}>
              {items.map((item) => {
                const pick = selected.get(item.id);
                return (
                  <li key={item.id} className={classes.pickerRow}>
                    <Checkbox
                      aria-label={k('select', { name: item.name })}
                      checked={!!pick}
                      onChange={(event) => toggle(item, event.currentTarget.checked)}
                    />
                    <span className={classes.pickerText}>
                      {item.name}
                      {item.specification ? ` · ${item.specification}` : ''}
                      <small>
                        {item.mainCategory} › {item.subCategory}
                        {item.companyCode ? ` · ${item.companyCode}` : ''}
                      </small>
                      {/* Gemide stok mevcudu (ADR 0111 §A; sahip: "5 adet mevcut desin") */}
                      {item.onHand !== undefined && item.onHand !== null && item.onHand > 0 ? (
                        <span className={classes.stockLine}>
                          <StatusBadge tone="info">
                            {t('purchasing.review.inStock', {
                              qty: formatQuantity(item.onHand, locale),
                            })}
                          </StatusBadge>
                        </span>
                      ) : null}
                    </span>
                    <NumberInput
                      className={classes.pickerQty}
                      aria-label={k('quantityOf', { name: item.name })}
                      min={0}
                      decimalScale={3}
                      hideControls
                      disabled={!pick}
                      value={pick?.quantity ?? ''}
                      onChange={(value) =>
                        setSelected((current) => {
                          const next = new Map(current);
                          next.set(item.id, {
                            item,
                            quantity: typeof value === 'number' && value > 0 ? value : '',
                          });
                          return next;
                        })
                      }
                      rightSection={<span className={classes.muted}>{item.unit}</span>}
                      rightSectionWidth={44}
                    />
                  </li>
                );
              })}
            </ul>
          )}
          <div className={classes.pickerFooter}>
            <span className={classes.muted} aria-live="polite">
              {k('selectedCount', { count: chosen.length })}
            </span>
            <Button
              leftSection={<IconPlus size={16} />}
              disabled={!catalogueValid || chosen.length > remaining}
              onClick={addCatalogue}
            >
              {k('addSelected', { count: chosen.length })}
            </Button>
          </div>
          {limitNote(chosen.length)}
          {catalogOnly ? null : (
            <form
              className={classes.freeItem}
              aria-label={k('freeTitle')}
              onSubmit={(event) => {
                event.preventDefault();
                if (!freeName.trim() || !freeUnit || remaining < 1) return;
                onAdd([{ materialId: null, name: freeName.trim(), unit: freeUnit, quantity: 1 }]);
                close();
              }}
            >
              <TextInput
                label={k('freeTitle')}
                placeholder={k('freeName')}
                value={freeName}
                maxLength={200}
                onChange={(event) => setFreeName(event.currentTarget.value)}
              />
              <Select
                label={k('freeUnit')}
                data={UNITS}
                value={freeUnit}
                allowDeselect={false}
                onChange={setFreeUnit}
              />
              <Button
                type="submit"
                variant="default"
                leftSection={<IconPlus size={16} />}
                disabled={!freeName.trim() || remaining < 1}
              >
                {k('freeAdd')}
              </Button>
            </form>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="paste" pt="sm">
          <Textarea
            label={k('pasteLabel')}
            description={k('pasteHint')}
            placeholder={k('pastePlaceholder')}
            autosize
            minRows={4}
            maxRows={10}
            value={pasted}
            onChange={(event) => {
              setPasted(event.currentTarget.value);
              setProposals(null);
            }}
            classNames={{ input: classes.pasteInput ?? '' }}
          />
          <div className={classes.pickerFooter}>
            <span className={classes.muted}>
              {parseNote?.header ? k('headerSkipped') : ''}
              {parseNote?.truncated ? ` ${k('truncated', { count: 200 })}` : ''}
            </span>
            <Button
              variant="default"
              loading={matching}
              disabled={!pasted.trim()}
              onClick={() => void match()}
            >
              {k('matchRows')}
            </Button>
          </div>
          {matchError ? (
            <LoadError title={k('error')} code={matchError} onRetry={() => void match()} />
          ) : null}
          {proposals && proposals.length === 0 ? <EmptyState title={k('nothingPasted')} /> : null}
          {proposals && proposals.length > 0 ? (
            <>
              <section
                className={`${table.tableWrap} ${classes.pasteWrap}`}
                aria-label={k('proposals')}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={`${table.table} ${classes.table}`}>
                  <thead>
                    <tr>
                      <th scope="col">
                        <span className={classes.visuallyHidden}>{k('include')}</span>
                      </th>
                      <th scope="col">{k('pasted')}</th>
                      <th scope="col">{k('catalogueItem')}</th>
                      <th scope="col">{k('qty')}</th>
                      <th scope="col">{k('freeUnit')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposals.map((p, index) => (
                      <tr key={`${p.row.line}-${p.row.code}`}>
                        <td>
                          <Checkbox
                            aria-label={k('includeRow', { name: p.row.code })}
                            checked={p.include}
                            onChange={(event) =>
                              setProposal(index, { include: event.currentTarget.checked })
                            }
                          />
                        </td>
                        <td className={classes.material}>{p.row.code}</td>
                        <td>
                          {p.material ? (
                            <span>
                              {p.material.name}
                              {p.material.companyCode ? (
                                <small className={classes.muted}> · {p.material.companyCode}</small>
                              ) : null}
                              {p.row.unitText && p.row.unit !== p.material.unit ? (
                                <small className={classes.pasteNote}>
                                  {k('unitFromCatalogue', { unit: p.material.unit })}
                                </small>
                              ) : null}
                            </span>
                          ) : (
                            <span>
                              <StatusBadge tone="warning">{k('freeProposal')}</StatusBadge>
                              {p.row.unitText && !p.row.unit ? (
                                <small className={classes.pasteNote}>
                                  {k('unknownUnit', { unit: p.row.unitText })}
                                </small>
                              ) : null}
                            </span>
                          )}
                        </td>
                        <td>
                          <NumberInput
                            className={classes.pickerQty}
                            aria-label={k('quantityOf', { name: p.row.code })}
                            min={0}
                            decimalScale={3}
                            hideControls
                            value={p.quantity}
                            error={p.include && p.quantity === '' ? true : undefined}
                            onChange={(value) =>
                              setProposal(index, {
                                quantity: typeof value === 'number' && value > 0 ? value : '',
                              })
                            }
                          />
                        </td>
                        <td>
                          {p.material ? (
                            p.material.unit
                          ) : (
                            <Select
                              className={classes.pickerUnit}
                              aria-label={k('unitOf', { name: p.row.code })}
                              data={UNITS}
                              value={p.unit}
                              allowDeselect={false}
                              onChange={(value) => setProposal(index, { unit: value ?? 'PCS' })}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
              <div className={classes.pickerFooter}>
                <span className={classes.muted}>
                  {k('matchedSummary', {
                    matched: proposals.filter((p) => p.material).length,
                    free: proposals.filter((p) => !p.material).length,
                  })}
                </span>
                <Button
                  leftSection={<IconPlus size={16} />}
                  disabled={!pasteValid || included.length > remaining}
                  onClick={addPasted}
                >
                  {k('addLines', { count: included.length })}
                </Button>
              </div>
              {limitNote(included.length)}
            </>
          ) : null}
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}
